import { classNameBindings } from "@ember-decorators/component"
import Component from "@ember/component"
import { action, computed, get } from "@ember/object"
import PromiseProxyMixin from "@ember/object/promise-proxy-mixin"
import ObjectProxy from "@ember/object/proxy"
import { later } from "@ember/runloop"
import { inject as service } from "@ember/service"
import standardsSearchBackToPaneAction from "cc-frontend/lib/actions/app/STANDARDS_SEARCH_BACK_TO_PANE"
import standardsSearchSelectJurisdictionAction from "cc-frontend/lib/actions/app/STANDARDS_SEARCH_SELECT_JURISDICTION"
import standardsSearchSelectSubjectAction from "cc-frontend/lib/actions/app/STANDARDS_SEARCH_SELECT_SUBJECT"
import _ from "lodash"
import { findIndex } from "lodash-es"
import * as R from "remeda"

@classNameBindings("wasInserted")
export default class StandardSet extends Component {
  @service("ember-store")
  store

  @service("dispatcher")
  dispatcher

  pane = "standards"

  commonCoreJurisdiction = {
    id: "67810E9EF6944F9383DCC602A3484C23",
    title: "Common Core State Standards",
    type: "state",
  }

  ngssJurisdiction = {
    id: "71E5AA409D894EB0B43A8CD82F727BFE",
    title: "Next Generation Science Standards",
    type: "organization",
  }

  collegeBoardJurisdiction = {
    id: "0A5FD99233A74D8FA3A74F52E5F6CDEC",
    title: "AP/College Board",
    type: "organization",
  }

  showStates = false
  showOrganizations = false
  showSchools = false
  jurisdictionId = null

  init() {
    super.init(...arguments)

    if (this.standardSetId !== undefined && this.standardSetId.match(/blank/) !== null) {
      if (this.initialJurisdictionId && this.initialJurisdictionId !== "NOT_LISTED") {
        this.set("jurisdictionId", this.initialJurisdictionId)
        this.set("pane", "subjects")
      } else {
        this.set("pane", "jurisdictions")
      }
    }
  }

  @computed("jurisdictions.[]")
  get stateJurisdictions() {
    return _.filter(this.jurisdictions, function (jurisdiction) {
      return get(jurisdiction, "type") === "state"
    })
  }

  @computed("jurisdictions.[]")
  get organizationJurisdictions() {
    return _.filter(this.jurisdictions, function (jurisdiction) {
      return (
        get(jurisdiction, "type") === "organization" && get(jurisdiction, "status") === "approved"
      )
    })
  }

  @computed("jurisdictions.[]")
  get schoolJurisdictions() {
    return _.filter(this.jurisdictions, function (jurisdiction) {
      return get(jurisdiction, "type") === "school" && get(jurisdiction, "status") === "approved"
    })
  }

  didInsertElement() {
    super.didInsertElement(...arguments)
    later(
      this,
      function () {
        return this.set("wasInserted", true)
      },
      10,
    )
  }

  @computed("standardSetId")
  get linkToSet() {
    return 'http://commonstandardsproject.com/search?ids=%5B"' + this.standardSetId + '"%5D'
  }

  @computed("paneIndex")
  get showRemoveButton() {
    return this.paneIndex !== 0
  }

  @computed("standardSetId")
  get standardSet() {
    if (this.standardSetId !== undefined && this.standardSetId.match(/blank/) !== null) return null
    var id = this.standardSetId

    class ObjectPromiseProxy extends ObjectProxy.extend(PromiseProxyMixin) {}

    return ObjectPromiseProxy.create({
      promise: get(this, "store").find("standard-set", id),
    })
  }

  @computed("standardSet.attributes.jurisdiction.id", "jurisdictionId")
  get jurisdiction() {
    var id =
      this.jurisdictionId ||
      this.get("standardSet.attributes.jurisdiction.id") ||
      this.get("initialJurisdictionId")
    if (id === undefined) return null

    class ObjectPromiseProxy extends ObjectProxy.extend(PromiseProxyMixin) {}

    return ObjectPromiseProxy.create({
      promise: get(this, "store").find("standard-jurisdiction", id),
    })
  }

  @computed("jurisdiction.attributes.standardSets")
  get publishedSubjects() {
    var sets = this.get("jurisdiction.attributes.standardSets") || []
    return R.pipe(
      sets,
      R.reject((set) => get(set, "document.publicationStatus") === "Deleted"),
      R.reject((set) => get(set, "document.publicationStatus") === "Archived"),
      R.reject((set) => get(set, "document.publicationStatus") === "Deprecated"),
      R.map((s) => s.subject),
      R.uniq(),
      R.sortBy((s) => s),
    )
  }

  @computed("jurisdiction.attributes.standardSets")
  get deprecatedSubjects() {
    var sets = this.get("jurisdiction.attributes.standardSets") || []
    return R.pipe(
      sets,
      R.filter((set) => get(set, "document.publicationStatus") === "Deprecated"),
      R.map((s) => s.subject),
      R.uniq(),
      R.sortBy((s) => s),
    )
  }
  @computed("jurisdiction.attributes.standardSets")
  get subjects() {
    var sets = this.get("jurisdiction.attributes.standardSets") || {}
    return _.chain(sets).map("subject").uniq().value().sort()
  }

  @computed("subject", "standardSet.attributes.subject", "jurisdiction.attributes.standardSets")
  get gradeLevels() {
    var sets = this.get("jurisdiction.attributes.standardSets") || {}
    var subject = this.subject || this.get("standardSet.attributes.subject")
    return _.chain(sets)
      .filter({ subject: subject })
      .reject((set) => get(set, "document.publicationStatus") === "Deleted")
      .reject((set) => get(set, "document.publicationStatus") === "Archived")
      .sortBy("title")
      .thru(function (arr) {
        let index = findIndex(arr, function (set) {
          return set.title == "Grade K" || set.title == "Kindergarten"
        })
        if (index !== -1) {
          let k = arr.splice(index, 1)
          arr.unshift(k[0])
        }
        return arr
      })
      .value()
  }

  @computed("standardSet.attributes.jurisdiction", "jurisdiction.attributes.title")
  get currentJurisdiction() {
    return (
      this.get("jurisdiction.attributes.title") ||
      this.get("standardSet.attributes.jurisdiction.title")
    )
  }

  @computed("standardSet.attributes.subject", "subject")
  get currentSubject() {
    return this.subject || this.get("standardSet.attributes.subject")
  }

  @computed("standardSet.attributes.standards", "standardSet.isPending")
  get standards() {
    if (this.get("standardSet.isPending") === true) return []
    var standards = _(this.get("standardSet.attributes.standards"))
      .values()
      .sortBy("position")
      .compact()
      .value()
    var results = this.results
    if (results !== null && results !== undefined) {
      standards = _.filter(standards, function (s) {
        return _.includes(results, s.id)
      })
    }
    return standards
  }

  @action
  selectJurisdiction(jurisdiction) {
    this.dispatcher.dispatchPreparedAction(
      standardsSearchSelectJurisdictionAction({
        jurisdictionId: jurisdiction.id,
        jurisdictionTitle: jurisdiction.title,
        jurisdictionType: jurisdiction.type,
      }),
    )
    this.set("jurisdictionId", jurisdiction.id)
    this.set("pane", "subjects")
    $(".modal").scrollTop(0)
  }

  @action
  selectSubject(subject) {
    this.dispatcher.dispatchPreparedAction(standardsSearchSelectSubjectAction({ subject: subject }))
    this.set("subject", subject)
    this.set("pane", "grade-levels")
    $(".modal").scrollTop(0)
  }

  @action
  onSelectSet(set) {
    this.selectSet(set.id, this.standardSetId)
    if (this.isDestroyed || this.isDestroying) return
    this.set("pane", "standards")
    $(".modal").scrollTop(0)
    this.set("standardSetId", set.id)
  }

  @action
  backToPane(pane) {
    this.dispatcher.dispatchPreparedAction(standardsSearchBackToPaneAction({}))
    this.set("pane", pane)
  }

  @action
  onRemoveSet() {
    window?.analytics?.track("Standards Search - Remove Set")
    this.removeSet(this.standardSetId)
  }

  @action
  toggleSection(section) {
    if (section === "states") {
      this.set("showStates", !this.get("showStates"))
    } else if (section === "organizations") {
      this.set("showOrganizations", !this.get("showOrganizations"))
    } else if (section === "schools") {
      this.set("showSchools", !this.get("showSchools"))
    }
  }

  @action
  toggleLinkToSet() {
    // analytics.track('Search - Show Link')
    this.toggleProperty("showLinkToSet")
  }

  @action
  onToggleStandard(standard) {
    this.toggleStandard(
      _.merge({}, standard, {
        jurisdiction: {
          title: this.get("jurisdiction.attributes.title"),
        },
        standardSet: {
          id: this.get("standardSet.id"),
          title: this.get("standardSet.attributes.title"),
          subject: this.get("standardSet.attributes.subject"),
        },
      }),
    )
  }
}
